import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { answerSchema, AnswerFormData } from "./answerSchema";
import { toast } from "sonner";
import { createTaskAnswer } from "../../api/request";
import { createFileAttachement } from "../../api/fileAttachement";
import { useState } from "react";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

type Props = {
  taskId: string;
  canReply: boolean;
  onSuccess?: (createdAnswer: any) => void;
};

const TaskAnswerForm = ({ taskId, canReply, onSuccess }: Props) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return; // ⬅️ solution clé

    const chosen = Array.from(files);
    if (chosen.some(file => file.size > 10 * 1024 * 1024 || !file.name.toLowerCase().endsWith('.pdf'))) {
      toast.error('Choisissez des PDF de 10 Mo maximum.');
      e.target.value = '';
      return;
    }
    setSelectedFiles((prev) => [...prev, ...chosen]);

    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AnswerFormData>({
    resolver: zodResolver(answerSchema),
    defaultValues: {
      taskId,
      message: "",
      statut_tache: "En Cours",
    },
  });

  if (!canReply) return null;

  const onSubmit = async (data: AnswerFormData) => {
    try {
      const finalStatus = data.statut_tache;
      const response = await createTaskAnswer(
        { id_tache: taskId, observation: data.message, statut_tache: finalStatus },
        buildPermissionContext(Resource.REQUEST, Action.UPDATE),
      );
      const answerId = response.data.id;

      const createdAnswer = response.data;

      // 2️⃣ Upload des pièces jointes (NON BLOQUANT)
      if (selectedFiles.length > 0) {
        try {
          await AddSupport(selectedFiles, answerId);
        } catch (error) {
          toast.error(
            "La réponse a été envoyée, mais une erreur est survenue lors de l’ajout des pièces jointes.",
          );
        }
      }

      // 3️⃣ Succès principal
      toast.success("Réponse envoyée avec succès");
      reset();
      setSelectedFiles([]);
      onSuccess?.({
        ...createdAnswer,
        statut_tache: finalStatus, // ✅ on force le statut si l’API ne le renvoie pas
      }); // 🔁 reload
    } catch (error: any) {
      // ❌ Erreur bloquante : réponse non créée
      toast.error(error.response?.data?.message || "Erreur lors de l’envoi de la réponse");
    }
  };

  // ajouter des supports de
  const AddSupport = async (files: File[], answerId: string) => {
    const formData = new FormData();

    files.forEach((file) => formData.append("files", file));
    formData.append("entity_id", answerId);
    formData.append("entity_type", "AnswerTache");

    await createFileAttachement(
      formData,
      buildPermissionContext(Resource.REQUEST, Action.UPDATE),
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h6 className="mb-2">
        <i className="ti ti-message-circle me-1" />
        Réponse
      </h6>

      <textarea
        className="form-control mb-2"
        rows={3}
        placeholder="Écrire une réponse..."
        {...register("message")}
      />

      {errors.message && (
        <small className="text-danger">{errors.message.message}</small>
      )}

      <div className="d-flex flex-column gap-2 mt-2">
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <label className="btn btn-sm btn-light border mb-0">
            <i className="ti ti-paperclip me-1" />
            {selectedFiles.length > 0
              ? "Ajouter un autre fichier"
              : "Ajouter un fichier"}
            <input type="file" accept="application/pdf,.pdf" hidden multiple onChange={handleFileChange} />
          </label>

          <div className="d-flex align-items-center gap-3">
            <div>
              <label htmlFor={`status-${taskId}`} className="form-label">État après cette réponse</label>
              <select id={`status-${taskId}`} className="form-select" {...register("statut_tache")}>
                <option value="En Cours">En cours — suivi intermédiaire</option>
                <option value="Traité">Traité favorablement</option>
                <option value="Rejeté">Rejeté</option>
              </select>
            </div>

            {/* Bouton envoyer */}
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              disabled={isSubmitting}
            >
              <i className="ti ti-send me-1" />
              Envoyer
            </button>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="d-flex flex-column gap-1">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="d-inline-flex align-items-center border rounded px-2 py-1 align-self-start"
              >
                <span className="text-truncate">
                  <i className="ti ti-file-text me-1" />
                  {file.name}
                </span>

                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger"
                  onClick={() => removeFile(index)}
                >
                  <i className="ti ti-x" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </form>
  );
};

export default TaskAnswerForm;
